/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { Fwaas, HostConfig, columns as fwaasColumns, shapeFwaasData } from "./FwaasData";
// Import from local request file - using explicit relative path to avoid conflicts
import * as FwaasRequest from "./request/request";
import { FwaasViewModal, FwaasEditModal } from "./FwaasModals";
import { buildPredicate, FilterField, labelId, parseToken } from "../../../../helpers/advancedFilters";
import { getTenants, Tenant } from "../../Tenants/request/request";

export default function FwaasRMPage() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  
  // Table data
  const [fwaasList, setFwaasList] = useState<Fwaas[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false); // Track if we're in create mode
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Loading state for Save button
  const [isCreating, setIsCreating] = useState(false); // Loading state for Create button
  const [selectedFwaas, setSelectedFwaas] = useState<Fwaas | null>(null);
  const [originalFwaas, setOriginalFwaas] = useState<Fwaas | null>(null); // Track original data for change detection
  const [selectedFwaasId, setSelectedFwaasId] = useState<number | null>(null);
  
  // Create form state
  const [createForm, setCreateForm] = useState<{
    tenantId: number | null;
    name: string;
    subscriptionId: string;
    terms: number | null;
    autoRenew: boolean | null;
    connections: string;
  }>({
    tenantId: null,
    name: "",
    subscriptionId: "",
    terms: null,
    autoRenew: null,
    connections: "",
  });
  
  // Tenant options for dropdown
  const [tenantOptions, setTenantOptions] = useState<Array<{ id: number; name: string; displayName: string }>>([]);

  // Delete confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // Loading state for Delete button
  const [fwaasToDelete, setFwaasToDelete] = useState<Fwaas | null>(null);

  // ===== 1) FETCH FWAAS LIST ON MOUNT =====
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    const fetchFwaasList = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        // Use the function from the local request file
        const resp = await FwaasRequest.getListFwaas(token);
        const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        const shapedData = shapeFwaasData(rawData);
        setFwaasList(shapedData);
      } catch (err) {
        console.error("Error fetching FWAAS list:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchFwaasList();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ===== 1.1) FETCH TENANTS FOR DROPDOWN =====
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    const fetchTenants = async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await getTenants(token);
        const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        const tenants = rawData.map((tenant: Tenant) => ({
          id: tenant.id,
          name: tenant.Name,
          displayName: tenant.nameDisplay || tenant.Name,
        }));
        setTenantOptions(tenants);
      } catch (err) {
        console.error("Error fetching tenants:", err);
      }
    };

    fetchTenants();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ===== 2) FETCH FWAAS DETAIL =====
  useEffect(() => {
    if (!selectedFwaasId || !isAuthenticated) return;

		const fetchFwaasDetail = async () => {
			try {
				setIsModalLoading(true);
				const token = await getAccessTokenSilently();
				const resp = await FwaasRequest.getFwaasById(token, selectedFwaasId);
				const fwaasData = (resp as any)?.data?.data ?? (resp as any)?.data;
				const shapedData = shapeFwaasData([fwaasData])[0];
				setSelectedFwaas(shapedData);
				setOriginalFwaas(JSON.parse(JSON.stringify(shapedData))); // Deep copy for comparison
			} catch (err) {
				console.error("Error fetching FWAAS detail:", err);
			} finally {
				setIsModalLoading(false);
			}
		};

    fetchFwaasDetail();
  }, [selectedFwaasId, isAuthenticated, getAccessTokenSilently]);

  // ===== 3) HANDLERS =====
  const handleViewFwaas = (f: Fwaas) => {
    setSelectedFwaasId(f.id);
    setIsViewModalOpen(true);
    setSelectedFwaas(null);
  };

  const handleEditFwaas = (f: Fwaas) => {
    setSelectedFwaasId(f.id);
    setIsCreateMode(false);
    setIsEditModalOpen(true);
    setSelectedFwaas(null);
    setOriginalFwaas(null);
  };

  const handleDeleteFwaas = (f: Fwaas) => {
    setFwaasToDelete(f);
    setIsAlertOpen(true);
  };

  const handleAddFwaas = () => {
    setSelectedFwaasId(null);
    setSelectedFwaas(null);
    setOriginalFwaas(null);
    setIsCreateMode(true);
    setIsModalLoading(false);
    setCreateForm({
      tenantId: null,
      name: "",
      subscriptionId: "",
      terms: null,
      autoRenew: null,
      connections: "",
    });
    setIsEditModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedFwaasId(null);
    setSelectedFwaas(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setIsCreateMode(false);
    setSelectedFwaasId(null);
    setSelectedFwaas(null);
    setOriginalFwaas(null);
    setCreateForm({
      tenantId: null,
      name: "",
      subscriptionId: "",
      terms: null,
      autoRenew: null,
      connections: "",
    });
  };

  const handleSaveFwaas = async () => {
    if (isCreateMode) {
      // Create flow
      if (!createForm.tenantId || !createForm.name || !createForm.subscriptionId || 
          createForm.terms === null || createForm.autoRenew === null || !createForm.connections) {
        return;
      }

      try {
        setIsCreating(true);
        const token = await getAccessTokenSilently();
        
        const payload: FwaasRequest.CreateFwaasPayload = {
          tenantId: createForm.tenantId,
          name: createForm.name,
          subscriptionId: createForm.subscriptionId,
          terms: createForm.terms,
          autoRenew: createForm.autoRenew,
          connections: createForm.connections,
        };

        await FwaasRequest.createFwaas(token, payload);
        
        // Refresh the list
        const resp = await FwaasRequest.getListFwaas(token);
        const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        const shapedData = shapeFwaasData(rawData);
        setFwaasList(shapedData);
        
        handleCloseEditModal();
      } catch (error) {
        console.error("Error creating FWAAS:", error);
      } finally {
        setIsCreating(false);
      }
    } else {
      // Update flow
      if (!selectedFwaas || !selectedFwaasId) return;

      try {
        setIsSaving(true);
        const token = await getAccessTokenSilently();
        
        // Build payload with only the editable fields
        const payload: FwaasRequest.UpdateFwaasPayload = {
          name: selectedFwaas.name,
          subscriptionId: selectedFwaas.subscriptionId,
          terms: selectedFwaas.terms,
          autoRenew: selectedFwaas.autoRenew,
          connections: selectedFwaas.connections,
          hostConfigs: selectedFwaas.hostConfigs,
        };

        await FwaasRequest.updateFwaasById(token, selectedFwaasId, payload);
        
        // Update the local list
        setFwaasList((prev) =>
          prev.map((f) => (f.id === selectedFwaasId ? selectedFwaas : f))
        );
        
        handleCloseEditModal();
      } catch (error) {
        console.error("Error saving FWAAS:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleFieldChange = (field: string, value: string | number | boolean | null | HostConfig[]) => {
    if (!selectedFwaas) return;
    setSelectedFwaas({
      ...selectedFwaas,
      [field]: value,
    });
  };

  // Check if there are changes between original and current FWAAS (for edit mode)
  const hasChanges = useMemo(() => {
    if (isCreateMode) return false; // Not applicable for create mode
    if (!selectedFwaas || !originalFwaas) return false;
    
    // Compare editable fields
    if (selectedFwaas.name !== originalFwaas.name) return true;
    if (selectedFwaas.subscriptionId !== originalFwaas.subscriptionId) return true;
    if (selectedFwaas.terms !== originalFwaas.terms) return true;
    if (selectedFwaas.autoRenew !== originalFwaas.autoRenew) return true;
    if (selectedFwaas.connections !== originalFwaas.connections) return true;
    
    // Compare hostConfigs
    if (selectedFwaas.hostConfigs.length !== originalFwaas.hostConfigs.length) return true;
    for (let i = 0; i < selectedFwaas.hostConfigs.length; i++) {
      const current = selectedFwaas.hostConfigs[i];
      const original = originalFwaas.hostConfigs[i];
      if (!original || current.host !== original.host || current.pve_host !== original.pve_host) {
        return true;
      }
    }
    
    return false;
  }, [selectedFwaas, originalFwaas, isCreateMode]);

  // Check if create form is valid (all required fields filled)
  const isCreateFormValid = useMemo(() => {
    return (
      createForm.tenantId !== null &&
      createForm.name.trim() !== "" &&
      createForm.subscriptionId.trim() !== "" &&
      createForm.terms !== null &&
      createForm.autoRenew !== null &&
      createForm.connections.trim() !== ""
    );
  }, [createForm]);

  // Handle create form field changes
  const handleCreateFieldChange = (field: string, value: string | number | boolean | null) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get tenant display name by ID
  const getTenantDisplayNameById = (id: number | null): string => {
    if (id === null) return "";
    const tenant = tenantOptions.find((t) => t.id === id);
    return tenant ? tenant.displayName : "";
  };

  const confirmDeleteFwaas = async () => {
    if (!fwaasToDelete) return;
    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      
      await FwaasRequest.deleteFwaasById(token, fwaasToDelete.id);
      
      // Update the local list
      setFwaasList((prev) => prev.filter((f) => f.id !== fwaasToDelete.id));
      
      setFwaasToDelete(null);
      setIsAlertOpen(false);
    } catch (error) {
      console.error("Error deleting FWAAS:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // ===== 4) FILTERING =====
  function makeFwaasFields(): FilterField<Fwaas>[] {
    return [
      { label: "ID", key: "id", type: "number", accessor: r => r.id },
      { label: "Name", key: "name", type: "string", accessor: r => r.name, inFreeText: true },
      { label: "Subscription ID", key: "subscriptionid", type: "string", accessor: r => r.subscriptionId, inFreeText: true },
      { label: "Tenant", key: "tenant", type: "string", accessor: r => r.tenant?.nameDisplay || r.tenant?.Name || "", inFreeText: true },
      { label: "Connections", key: "connections", type: "string", accessor: r => r.connections, inFreeText: true },
      {
        label: "Status",
        key: "status",
        type: "boolean",
        accessor: r => !r.isDraft, // Active = true, Draft = false
      },
    ];
  }

  const fwaasFields = useMemo(() => makeFwaasFields(), []);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Fwaas>>();
    for (const f of fwaasFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [fwaasFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of fwaasFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [fwaasFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Fwaas>(normalizedFilters, fwaasFields),
    [normalizedFilters, fwaasFields]
  );

  const filteredFwaas = useMemo(
    () => fwaasList.filter(predicate),
    [fwaasList, predicate]
  );

  const suggestedKeys = useMemo(() => {
    return fwaasFields.map(f => f.label);
  }, [fwaasFields]);

  // Override "Actions" column
  const baseColumns = fwaasColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: Fwaas) => (
          <div className="flex items-center gap-2">
            {/* View */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleViewFwaas(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>

            {/* Edit */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleEditFwaas(row)}
            >
              <IconEdit className="w-10 h-10" />
            </button>

            {/* Delete */}
            <button
              className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
              onClick={() => handleDeleteFwaas(row)}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  return (
    <GenericCard isCollapsible={false}>
      {/* Header with Searchbar and Add Button */}
      <div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>Create new FWAAS</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={handleAddFwaas}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-5 mb-5">
        {isFetching ? (
          <div className="space-y-2">
            <SkeletonTableRows rows={5} cols={6} />
          </div>
        ) : (
          <GenericTable
            columns={baseColumns}
            data={filteredFwaas}
            width="100%"
            initialRowsPerPage={5}
            rowsPerPageOptions={[5, 10]}
            tableClassName="bg-semantic-bg-primary"
          />
        )}
      </div>

      {/* View Modal */}
      <FwaasViewModal
        isOpen={isViewModalOpen}
        isLoading={isModalLoading}
        fwaas={selectedFwaas}
        onClose={handleCloseViewModal}
      />

      {/* Edit/Create Modal */}
      <FwaasEditModal
        isOpen={isEditModalOpen}
        isLoading={isModalLoading}
        isCreateMode={isCreateMode}
        fwaas={selectedFwaas}
        createForm={createForm}
        tenantOptions={tenantOptions}
        hasChanges={hasChanges}
        isFormValid={isCreateFormValid}
        isSaving={isSaving}
        isCreating={isCreating}
        getTenantDisplayNameById={getTenantDisplayNameById}
        onClose={handleCloseEditModal}
        onSave={handleSaveFwaas}
        onFieldChange={handleFieldChange}
        onCreateFieldChange={handleCreateFieldChange}
      />

      {/* Delete Confirmation Alert */}
      {isAlertOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="mb-4">
            Are you sure you want to delete FWAAS &quot;{fwaasToDelete?.name}&quot;?
            </p>
            <div className="flex justify-end gap-2">
              <GenericButton
                label="Cancel"
                variant="secondary"
                onClick={() => {
                  setIsAlertOpen(false);
                  setFwaasToDelete(null);
                }}
                disabled={isDeleting}
              />
              <GenericButton
                label="Delete"
                variant="danger"
                onClick={confirmDeleteFwaas}
                loading={isDeleting}
                disabled={isDeleting}
              />
            </div>
          </div>
        </div>
      )}
    </GenericCard>
  );
}

